import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCircle, X, Clock, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../api/client';

export function AlertNotificationBadge() {
  const [alertCount, setAlertCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchAlerts();
    
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle clicks outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchAlerts() {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/alert-preferences/filtered');
      setAlerts(response.alerts || []);
      setAlertCount(response.alerts?.length || 0);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }

  const navigateToSettings = () => {
    setShowDropdown(false);
    // In a real app, you'd use react-router
    console.log('Navigate to /settings/alerts');
  };

  const navigateToAllAlerts = () => {
    setShowDropdown(false);
    // In a real app, you'd use react-router
    console.log('Navigate to /alerts');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title={`${alertCount} active alerts`}
      >
        <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        
        {/* Badge showing alert count */}
        {alertCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 
                         bg-red-500 text-white text-xs font-bold
                         rounded-full flex items-center justify-center
                         animate-pulse">
            {alertCount > 9 ? '9+' : alertCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 rounded-lg bg-white dark:bg-gray-800 
                       shadow-xl border border-gray-200 dark:border-gray-700 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Notifications
              {loading && (
                <span className="ml-2 text-xs text-gray-500">Loading...</span>
              )}
            </h3>
            <button
              onClick={navigateToSettings}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline
                       flex items-center gap-1"
            >
              <Settings className="w-3 h-3" />
              Settings
            </button>
          </div>
          
          {/* Alert List */}
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No alerts - everything looks good!
                </p>
              </div>
            ) : (
              alerts.map((alert) => (
                <AlertNotificationItem
                  key={`${alert.type}-${alert.id}`}
                  alert={alert}
                  onDismiss={() => fetchAlerts()}
                />
              ))
            )}
          </div>
          
          {/* Footer */}
          {alerts.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={navigateToAllAlerts}
                className="w-full text-sm text-blue-600 dark:text-blue-400 
                         hover:underline text-center py-2"
              >
                View all alerts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Alert Notification Item
function AlertNotificationItem({ alert, onDismiss }) {
  const [showActions, setShowActions] = useState(false);

  async function handleSnooze(duration) {
    try {
      await api.post('/api/v1/alert-preferences/snooze', {
        alert_type: alert.type,
        alert_id: alert.id,
        snooze_duration: duration
      });
      
      toast.success('Alert snoozed');
      if (onDismiss) onDismiss();
    } catch (error) {
      console.error('Error snoozing alert:', error);
      toast.error('Failed to snooze alert');
    }
  }

  async function handleDismiss() {
    try {
      await api.post('/api/v1/alert-preferences/dismiss', {
        alert_type: alert.type,
        alert_id: alert.id
      });
      
      toast.success('Alert dismissed');
      if (onDismiss) onDismiss();
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  }

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      case 'warning':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div
      className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-800 last:border-0
                 transition-colors cursor-pointer"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-start gap-3">
        <div className={`
          flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg
          ${getSeverityConfig(alert.severity)}
        `}>
          {alert.icon || '🔔'}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {alert.title}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
            {alert.message}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {alert.created_at && formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
          </p>
        </div>
        
        {/* Quick Actions */}
        {showActions && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleSnooze(60)}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Snooze 1 hour"
            >
              <Clock className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={handleDismiss}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Dismiss"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AlertNotificationBadge;